import { useRef, useState, useEffect, ReactNode } from 'react'
import useWindowSize from '../../hooks/useWindowSize'
import { lightImpactVibrate } from '../../utils/appBridge'

type Tab = {
  id: string | number
  isDisabled?: boolean
  label?: string
  labelComponent?: ReactNode
  testLocator?: string
}

type TabsPropsTypes = {
  tabsData: Tab[]
  selectedTabId: Tab['id']
  selectedTabClassName?: string
  tabClassName?: string
  tabsClassName?: string
  isFullWidth?: boolean
  onTabClick: (tabId: Tab['id']) => void
  /** Optional prefix for tab button ids (tabpanel aria-labelledby). */
  tabButtonIdPrefix?: string
}

export default function Tabs({
  tabsData,
  selectedTabId,
  tabClassName,
  tabsClassName,
  selectedTabClassName,
  isFullWidth,
  onTabClick,
  tabButtonIdPrefix
}: TabsPropsTypes) {
  const tabsRef = useRef<{ [x: string | number]: HTMLElement | null } | []>([])
  const [activeTabId, setActiveTabId] = useState<Tab['id'] | null>(null)
  const [tabUnderlineWidth, setTabUnderlineWidth] = useState(0)
  const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0)
  const [enableTransition, setEnableTransition] = useState(false)
  const { width } = useWindowSize()

  useEffect(() => {
    if (activeTabId === null) {
      return
    }

    const setTabPosition = () => {
      // @ts-ignore
      const currentTab = tabsRef?.current?.[`${activeTabId}`] as HTMLElement
      if (currentTab) {
        setTabUnderlineLeft(currentTab?.offsetLeft ?? 0)
        setTabUnderlineWidth(currentTab?.clientWidth ?? 0)
        setTimeout(() => {
          setEnableTransition(true)
        }, 50)
      }
    }

    setTimeout(() => {
      setTabPosition()
    }, 50)
  }, [activeTabId, width])

  useEffect(() => {
    setActiveTabId(() => selectedTabId ?? null)
  }, [selectedTabId])

  const tabCount = tabsData.length

  return (
    <div
      role='tablist'
      className={`flew-row relative mx-auto flex min-h-9 h-full text-[#6A6E6E] rounded-[12px]
     bg-[#EBF2F2] px-[3px] backdrop-blur-sm ${isFullWidth ? 'w-full' : 'w-max'} ${tabsClassName ?? ''}`}
    >
      <span
        aria-hidden='true'
        className={`absolute bottom-0 top-0 -z-10 flex overflow-hidden py-[3px] ${enableTransition ? 'transition-all ease-in-out duration-300' : ''}`}
        style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
      >
        <span className='h-full w-full rounded-[10px] bg-white' />
      </span>
      {tabsData.map(({ id, label, labelComponent, isDisabled, testLocator }, idx) => {
        const isActive = id === selectedTabId
        const tabName =
          typeof label === 'string' ? label : typeof id === 'string' ? id : `Tab ${idx + 1}`
        return (
          <button
            key={id}
            type='button'
            role='tab'
            aria-selected={isActive}
            aria-posinset={idx + 1}
            aria-setsize={tabCount}
            // iOS Details_B17 / B26 / Android B10: never put "selected" or "tab" in
            // aria-label — VoiceOver/TalkBack already announce those from
            // aria-selected and role="tab" (duplicate → "selected"/"tab" twice).
            aria-label={isActive ? tabName : `${tabName}, double tap to select`}
            id={tabButtonIdPrefix ? `${tabButtonIdPrefix}-${id}` : undefined}
            disabled={isDisabled}
            ref={(el) => {
              // @ts-ignore
              tabsRef.current[id] = el
            }}
            // min-h/w-6 (24px) + self-stretch: WCAG 2.5.8 / Reg_Details_B05 (was min-h-0 ≈20dp)
            className={`flex flex-1 self-stretch items-center justify-center outline-none focus:outline-none border-none
            text-13 cursor-pointer select-none rounded-full px-0 min-h-6 min-w-6 py-0 text-center font-light bg-transparent
            ${isDisabled ? 'opacity-[0.6]' : ''} ${
              isActive ? `text-[#232323] ${selectedTabClassName ?? ''}` : `text-inherit ${tabClassName ?? ''}`
            } `}
            test-locator={testLocator}
            onClick={() => {
              lightImpactVibrate()
              setActiveTabId(id)
              onTabClick(id)
            }}
          >
            <span aria-hidden='true'>
              {labelComponent ?? (
                <div
                  className={`${id === selectedTabId ? `font-[500]` : `font-[400]`} 
                pr-[2px]`}
                >
                  {label}
                </div>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
